import React, { useState, useEffect, useCallback } from "react";
import { AlertTriangle, CheckCircle2, XCircle, ArrowUpRight, History, ExternalLink } from "lucide-react";
import { createWithdrawal, getWithdrawals } from "../../lib/investApi";

const CATEGORY_LABEL = {
  SUV: "SUV Token",
  SEDAN: "Sedan Token",
  HATCHBACK: "Hatchback Token",
  TRUCK: "Truck Token",
  SPORTS: "Sports Token",
};

const STATUS_CONFIG = {
  PENDING:   { label: "Pending",   bg: "#FEF3C7", color: "#92400E" },
  COMPLETED: { label: "Completed", bg: "#D1FAE5", color: "#065F46" },
  FAILED:    { label: "Failed",    bg: "#FEE2E2", color: "#991B1B" },
};

function truncate(str, n = 10) {
  if (!str || str.length <= n * 2 + 3) return str;
  return `${str.slice(0, n)}…${str.slice(-6)}`;
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

function FieldLabel({ children, hint }) {
  return (
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, letterSpacing: "0.03em", color: "var(--zc-text-2)", marginBottom: 6 }}>
      {children}
      {hint && <span style={{ fontWeight: 400, color: "var(--zc-text-3)", marginLeft: 6 }}>{hint}</span>}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  height: 42,
  padding: "0 13px",
  borderRadius: 9,
  border: "1.5px solid var(--zc-border-2)",
  background: "var(--zc-bg-2)",
  color: "var(--zc-text)",
  fontSize: 14,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

export default function InvestWithdrawTab({ walletUserId, holdings = [], onWithdrawComplete }) {
  const [selectedTokenId, setSelectedTokenId] = useState("");
  const [amount, setAmount]       = useState("");
  const [toAddress, setToAddress] = useState("");
  const [busy, setBusy]           = useState(false);
  const [result, setResult]       = useState(null);
  const [formError, setFormError] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wLoading, setWLoading]   = useState(false);

  const selectedHolding = holdings.find(h => String(h.tokenId) === String(selectedTokenId));

  const loadWithdrawals = useCallback(async () => {
    if (!walletUserId) return;
    setWLoading(true);
    try {
      const data = await getWithdrawals(walletUserId);
      setWithdrawals(Array.isArray(data) ? data : []);
    } catch {}
    finally { setWLoading(false); }
  }, [walletUserId]);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setResult(null);

    if (!selectedTokenId) { setFormError("Select a token to withdraw."); return; }
    const amt = Number(amount);
    if (!amt || amt < 1) { setFormError("Amount must be at least 1."); return; }
    if (selectedHolding && amt > selectedHolding.balance) { setFormError(`Max available: ${selectedHolding.balance}`); return; }
    if (!/^0x[a-fA-F0-9]{40}$/.test(toAddress.trim())) { setFormError("Enter a valid 0x Ethereum address."); return; }

    setBusy(true);
    try {
      const data = await createWithdrawal(walletUserId, Number(selectedTokenId), amt, toAddress.trim());
      setResult({ ok: true, data });
      setAmount("");
      setToAddress("");
      loadWithdrawals();
      onWithdrawComplete?.();
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!walletUserId) {
    return (
      <div style={{ textAlign: "center", padding: "72px 0", color: "var(--zc-text-3)" }}>
        <ArrowUpRight size={36} style={{ margin: "0 auto 14px", display: "block", opacity: 0.35 }} />
        <p style={{ fontSize: 14 }}>Sign in to withdraw tokens.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, alignItems: "start", maxWidth: 900 }}>

      {/* ── Withdraw form card ── */}
      <div className="zc-card" style={{ padding: 0, overflow: "hidden" }}>
        {/* Header */}
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid var(--zc-border)",
          background: "var(--zc-bg-2)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: "var(--zc-secondary-soft)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ArrowUpRight size={15} style={{ color: "var(--zc-secondary)" }} />
          </div>
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>Withdraw Tokens</span>
        </div>

        <div style={{ padding: "20px" }}>
          {holdings.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, background: "var(--zc-bg-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 12px", fontSize: 22,
              }}>🪙</div>
              <p style={{ fontSize: 13, color: "var(--zc-text-3)" }}>You have no tokens to withdraw yet.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Token selector */}
              <div>
                <FieldLabel>Token</FieldLabel>
                <select
                  value={selectedTokenId}
                  onChange={e => setSelectedTokenId(e.target.value)}
                  required
                  style={{ ...inputStyle, cursor: "pointer", appearance: "none",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B6B75' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 13px center",
                    paddingRight: 36,
                  }}
                >
                  <option value="">Choose a token…</option>
                  {holdings.map(h => (
                    <option key={h.tokenId} value={h.tokenId}>
                      {CATEGORY_LABEL[h.category] ?? h.name} ({h.symbol}) — {h.balance} available
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <FieldLabel hint={selectedHolding ? `max ${selectedHolding.balance}` : undefined}>Amount</FieldLabel>
                <div style={{ position: "relative" }}>
                  <input
                    type="number"
                    min={1}
                    max={selectedHolding?.balance}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="1"
                    required
                    style={inputStyle}
                  />
                  {selectedHolding && (
                    <button
                      type="button"
                      onClick={() => setAmount(String(selectedHolding.balance))}
                      style={{
                        position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                        padding: "3px 8px", borderRadius: 5,
                        background: "var(--zc-secondary-soft)",
                        color: "var(--zc-secondary)",
                        border: "none", cursor: "pointer",
                      }}
                    >
                      MAX
                    </button>
                  )}
                </div>
              </div>

              {/* Destination */}
              <div>
                <FieldLabel>Destination Address</FieldLabel>
                <input
                  type="text"
                  value={toAddress}
                  onChange={e => setToAddress(e.target.value)}
                  placeholder="0x…"
                  required
                  style={{ ...inputStyle, fontFamily: "var(--zc-font-mono)", fontSize: 12 }}
                />
              </div>

              {/* Gas warning */}
              <div style={{
                display: "flex", gap: 10, padding: "11px 14px",
                background: "#FFFBEB",
                border: "1px solid #FDE68A",
                borderRadius: 9, fontSize: 12, color: "#92400E",
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Gas fees will be deducted from your wallet ETH balance.</span>
              </div>

              {/* Inline error */}
              {formError && (
                <div style={{
                  display: "flex", gap: 8, alignItems: "center",
                  padding: "10px 13px", borderRadius: 9,
                  background: "#FEF2F2", border: "1px solid #FECACA",
                  color: "var(--zc-danger)", fontSize: 13,
                }}>
                  <XCircle size={14} style={{ flexShrink: 0 }} />
                  {formError}
                </div>
              )}

              {/* Result feedback */}
              {result && (
                <div style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "12px 14px", borderRadius: 9,
                  background: result.ok ? "#D1FAE5" : "#FEF2F2",
                  border: `1px solid ${result.ok ? "#A7F3D0" : "#FECACA"}`,
                }}>
                  {result.ok
                    ? <CheckCircle2 size={16} style={{ color: "var(--zc-success)", flexShrink: 0, marginTop: 1 }} />
                    : <XCircle size={16} style={{ color: "var(--zc-danger)", flexShrink: 0, marginTop: 1 }} />
                  }
                  <div style={{ fontSize: 13 }}>
                    {result.ok ? (
                      <>
                        <div style={{ fontWeight: 600, color: "#065F46", marginBottom: 4 }}>Withdrawal submitted</div>
                        {result.data?.txHash && (
                          <div style={{ fontFamily: "var(--zc-font-mono)", fontSize: 11, color: "#047857" }}>
                            {truncate(result.data.txHash, 14)}
                          </div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: "#991B1B" }}>{result.message}</span>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="zc-btn-secondary"
                style={{ width: "100%", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}
                disabled={busy}
              >
                {busy ? (
                  <>
                    <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
                    Submitting…
                  </>
                ) : (
                  <><ArrowUpRight size={15} />Withdraw Tokens</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ── Recent withdrawals card ── */}
      <div className="zc-card" style={{ padding: 0, overflow: "hidden" }}>
        {/* Header */}
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid var(--zc-border)",
          background: "var(--zc-bg-2)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: "var(--zc-secondary-soft)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <History size={15} style={{ color: "var(--zc-secondary)" }} />
          </div>
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>Recent Withdrawals</span>
          {withdrawals.length > 0 && (
            <span style={{
              marginLeft: "auto",
              fontSize: 11, fontWeight: 600,
              padding: "2px 8px", borderRadius: 20,
              background: "var(--zc-secondary-soft)",
              color: "var(--zc-secondary)",
            }}>
              {withdrawals.length}
            </span>
          )}
        </div>

        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
          {wLoading ? (
            [1, 2, 3].map(i => (
              <div key={i} style={{ height: 64, borderRadius: 10, background: "var(--zc-bg-3)", animation: "pulse 1.5s ease-in-out infinite" }} />
            ))
          ) : withdrawals.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, background: "var(--zc-bg-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 12px",
              }}>
                <ExternalLink size={20} style={{ color: "var(--zc-text-3)", opacity: 0.5 }} />
              </div>
              <p style={{ fontSize: 13, color: "var(--zc-text-3)" }}>No withdrawals yet.</p>
            </div>
          ) : (
            withdrawals.slice(0, 10).map(w => {
              const sc = STATUS_CONFIG[w.status] ?? { label: w.status, bg: "var(--zc-bg-3)", color: "var(--zc-text-2)" };
              return (
                <div
                  key={w.id}
                  style={{
                    padding: "11px 14px",
                    background: "var(--zc-bg-3)",
                    borderRadius: 10,
                    border: "1px solid var(--zc-border)",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--zc-bg-2)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "var(--zc-bg-3)"; }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>
                      Token #{w.tokenId} · {w.amount} tokens
                    </span>
                    <span style={{
                      ...sc,
                      padding: "3px 9px", borderRadius: 20,
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {sc.label}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ fontSize: 11, color: "var(--zc-text-3)", fontFamily: "var(--zc-font-mono)", display: "flex", alignItems: "center", gap: 4 }}>
                      <ArrowUpRight size={10} style={{ opacity: 0.6 }} />
                      {truncate(w.toAddress, 10)}
                    </div>
                    {w.txHash && (
                      <div style={{ fontSize: 10, color: "var(--zc-text-3)", fontFamily: "var(--zc-font-mono)" }}>
                        tx: {truncate(w.txHash, 10)}
                      </div>
                    )}
                    {w.createdAt && (
                      <div style={{ fontSize: 10, color: "var(--zc-text-3)", marginTop: 1 }}>{fmtDate(w.createdAt)}</div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
